import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getBillingStatus, createPortalSession } from '../api/client';
import { usePermissions } from '../contexts/PermissionContext';
import {
  CreditCard, CheckCircle, AlertTriangle, Clock, Users, ClipboardList, Zap,
} from 'lucide-react';

const STATUS_CONFIG = {
  active:   { label: 'Active',    color: '#10B981', bg: '#D1FAE5' },
  trialing: { label: 'Free Trial', color: '#2563EB', bg: '#DBEAFE' },
  past_due: { label: 'Past Due',   color: '#F59E0B', bg: '#FEF3C7' },
  canceled: { label: 'Canceled',   color: '#EF4444', bg: '#FEE2E2' },
  inactive: { label: 'Inactive',   color: '#6B7280', bg: '#F3F4F6' },
};

function UsageBar({ label, current, max, icon: Icon, color }) {
  const unlimited = max === -1;
  const pct = unlimited ? 0 : Math.min(100, Math.round((current / max) * 100));
  const warn = !unlimited && pct >= 80;

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>
          <Icon size={14} color={color} /> {label}
        </span>
        <span style={{ fontSize: 13, color: warn ? '#EF4444' : '#6B7280' }}>
          {current} / {unlimited ? '∞' : max}
        </span>
      </div>
      {!unlimited && (
        <div style={{ height: 8, background: '#F3F4F6', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 4,
            width: `${pct}%`,
            background: pct >= 90 ? '#EF4444' : pct >= 70 ? '#F59E0B' : color,
            transition: 'width 0.4s',
          }} />
        </div>
      )}
      {unlimited && (
        <div style={{ fontSize: 12, color: '#10B981', fontWeight: 500 }}>Unlimited</div>
      )}
    </div>
  );
}

export default function Billing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [toast, setToast] = useState('');

  const checkoutResult = searchParams.get('checkout');

  useEffect(() => {
    getBillingStatus()
      .then(r => setBilling(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (checkoutResult === 'success') {
      setToast('🎉 Subscription activated! Welcome to your new plan.');
    } else if (checkoutResult === 'cancelled') {
      setToast('Checkout cancelled. Your plan has not changed.');
    }
    if (checkoutResult) {
      const t = setTimeout(() => setToast(''), 5000);
      return () => clearTimeout(t);
    }
  }, [checkoutResult]);

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const res = await createPortalSession();
      window.location.href = res.data.url;
    } catch (err) {
      setToast(err.response?.data?.detail || 'Could not open portal. Please try again.');
      setPortalLoading(false);
    }
  };

  const { hasPermission } = usePermissions();
  const canManage = hasPermission('billing:manage');

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60, color: '#6B7280' }}>Loading billing info...</div>;
  }

  if (!billing) {
    return <div style={{ textAlign: 'center', padding: 60, color: '#EF4444' }}>Failed to load billing information.</div>;
  }

  const statusCfg = STATUS_CONFIG[billing.subscription_status] || STATUS_CONFIG.inactive;
  const planName = billing.plan_info?.name || billing.plan;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 24 }}>
        Billing &amp; Subscription
      </h1>

      {/* Toast */}
      {toast && (
        <div style={{
          background: toast.startsWith('🎉') ? '#D1FAE5' : '#FEF3C7',
          border: `1px solid ${toast.startsWith('🎉') ? '#6EE7B7' : '#FCD34D'}`,
          color: '#111827', padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 14,
        }}>
          {toast}
        </div>
      )}

      {/* Current plan card */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #F3F4F6', marginBottom: 20,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>
                {planName} Plan
              </h2>
              <span style={{
                background: statusCfg.bg, color: statusCfg.color,
                padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              }}>
                {statusCfg.label}
              </span>
            </div>
            <p style={{ color: '#6B7280', fontSize: 14, margin: 0 }}>
              ${billing.plan_info?.price_monthly}/month
            </p>
          </div>

          {canManage && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {billing.has_active_subscription ? (
                <button
                  onClick={handlePortal}
                  disabled={portalLoading}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 18px', background: '#fff', border: '1.5px solid #D1D5DB',
                    borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#374151',
                  }}
                >
                  <CreditCard size={15} />
                  {portalLoading ? 'Opening...' : 'Manage Subscription'}
                </button>
              ) : null}
              <button
                onClick={() => navigate('/upgrade')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 18px', background: '#2563EB', border: 'none',
                  borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff',
                }}
              >
                <Zap size={15} />
                {billing.has_active_subscription ? 'Change Plan' : 'Upgrade Now'}
              </button>
            </div>
          )}
        </div>

        {/* Trial countdown */}
        {billing.subscription_status === 'trialing' && billing.is_active && (
          <div style={{
            marginTop: 16, background: '#EFF6FF', borderRadius: 8, padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <Clock size={14} color="#2563EB" />
            <span style={{ fontSize: 13, color: '#1D4ED8' }}>
              {billing.days_left_in_trial > 0
                ? `${billing.days_left_in_trial} day${billing.days_left_in_trial === 1 ? '' : 's'} remaining in your free trial`
                : 'Your trial expires today'}
              {billing.trial_end_date && (
                <> · ends {new Date(billing.trial_end_date).toLocaleDateString()}</>
              )}
            </span>
          </div>
        )}

        {/* Trial expired */}
        {billing.subscription_status === 'trialing' && !billing.is_active && (
          <div style={{
            marginTop: 16, background: '#FEF2F2', borderRadius: 8, padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <AlertTriangle size={14} color="#DC2626" />
            <span style={{ fontSize: 13, color: '#991B1B' }}>
              Your free trial has expired. Subscribe to restore access.
            </span>
          </div>
        )}
      </div>

      {/* Usage card */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #F3F4F6', marginBottom: 20,
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#111827', marginBottom: 20 }}>Usage This Period</h3>
        <UsageBar
          label="Technicians"
          current={billing.current_technicians}
          max={billing.plan_info?.max_technicians}
          icon={Users}
          color="#2563EB"
        />
        <UsageBar
          label="Work Orders"
          current={billing.current_work_orders}
          max={billing.plan_info?.max_work_orders}
          icon={ClipboardList}
          color="#10B981"
        />
      </div>

      {/* Plan features */}
      {billing.plan_info?.features?.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: 12, padding: 28,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #F3F4F6',
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#111827', marginBottom: 16 }}>
            Your Plan Includes
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: 10 }}>
            {billing.plan_info.features.map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151' }}>
                <CheckCircle size={14} color="#10B981" /> {f}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
